
import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

export default function PricingFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'Can I try AgenticAI before committing to a plan?',
      answer:
        'Absolutely! We offer a 14-day free trial with full access to all features. No credit card required. You can explore the platform and see if it fits your needs before making any commitment.',
    },
    {
      question: 'What happens when I reach my content creation limit?',
      answer:
        'When you approach your monthly limit, we\'ll notify you in advance. You can either upgrade to a higher plan or purchase additional content credits. Your existing scheduled posts will continue to publish as planned.',
    },
    {
      question: 'Can I change or cancel my plan at any time?',
      answer:
        'Yes! You can upgrade, downgrade, or cancel your subscription at any time. If you upgrade, you\'ll get immediate access to new features. If you downgrade, changes take effect at the start of your next billing cycle.',
    },
    {
      question: 'Which social media platforms do you support?',
      answer:
        'We support all major platforms including Facebook, Instagram, Twitter/X, LinkedIn, TikTok, Pinterest, and YouTube. Our AI adapts content specifically for each platform\'s best practices and audience.',
    },
    {
      question: 'Is my data secure with AgenticAI?',
      answer:
        'Security is our top priority. We use bank-level encryption (AES-256) for all data, comply with GDPR and SOC 2 standards, and never share your content or data with third parties. Your social media credentials are encrypted and stored securely.',
    },
    {
      question: 'Do you offer discounts for annual subscriptions?',
      answer:
        'Yes! Annual subscriptions come with a 20% discount compared to monthly billing. Plus, you\'ll get priority support and early access to new features.',
    },
    {
      question: 'How does the AI content creation work?',
      answer:
        'Our AI analyzes your brand voice, industry trends, and audience engagement patterns to generate relevant, high-quality content. You maintain full control and can edit any generated content before scheduling.',
    },
    {
      question: 'What kind of support do you provide?',
      answer:
        'All plans include email support with 24-hour response time. Professional and Business plans get priority support. Enterprise plans include a dedicated account manager, phone support, and custom onboarding.',
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-question-line mr-2"></i>
            FAQ
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-700">
            Everything you need to know about our pricing and plans
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#66b3ff] transition-colors"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left bg-white hover:bg-[#e4f4ff]/10 transition-colors cursor-pointer"
              >
                <h3 className="text-lg font-semibold text-[#22577A] pr-8">
                  {faq.question}
                </h3>
                <i
                  className={`ri-arrow-${
                    openIndex === index ? 'up' : 'down'
                  }-s-line text-2xl text-[#38a3a5] flex-shrink-0 transition-transform`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-6 py-5 bg-[#e4f4ff]/5 border-t border-gray-200">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Still have questions?</p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-[#22577A] font-semibold hover:text-[#38a3a5] transition-colors cursor-pointer"
          >
            Contact our sales team
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
